// lib/telemetry.js
// Frontend-observability (roadmap 1B): Web Vitals RUM + client error monitoring.
// Vitals worden per pageview gebatcht en bij pagehide/hidden via keepalive-fetch
// naar de backend gestuurd; errors gaan direct (gededuplicieerd en gecapt).
// Web Vitals gaan daarnaast als tagged event naar het bestaande Plausible-kanaal.

export const RELEASE = process.env.NEXT_PUBLIC_RELEASE || 'dev';

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || '/api';

// Dezelfde pagina-indeling als het dashboard: de kernflows + rest. Sort moet
// vóór de algemene set-route staan, anders verdwijnt deze flow in `set`.
export function classifyPage(pathname) {
  if (!pathname) return 'other';
  if (pathname === '/') return 'home';
  if (/^\/set\/[^/]+\/sort(?:\/|$)/.test(pathname)) return 'sort';
  if (pathname.startsWith('/set/')) return 'set';
  if (pathname.startsWith('/findmybag')) return 'findmybag';
  if (pathname.startsWith('/matchparts')) return 'matchparts';
  return 'other';
}

export function deviceType() {
  if (typeof window === 'undefined') return 'desktop';
  return window.innerWidth < 768 ? 'mobile' : 'desktop';
}

function post(path, payload) {
  try {
    // keepalive: overleeft pagehide (in tegenstelling tot een gewone fetch)
    fetch(`${API_BASE_URL}/telemetry/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // telemetrie mag nooit de app breken
  }
}

// --- Product events ---

// Tagged Plausible-event; props moeten platte strings/getallen zijn
export function trackEvent(name, props) {
  if (typeof window === 'undefined') return;
  try {
    window.plausible?.(name, props ? { props } : undefined);
  } catch {
    // telemetrie mag nooit de app breken
  }
}

// --- Web Vitals ---

const vitalsQueue = [];
let flushHooked = false;

function flushVitals() {
  if (vitalsQueue.length === 0) return;
  post('vitals', { metrics: vitalsQueue.splice(0), release: RELEASE });
}

export function queueVital(metric) {
  if (typeof window === 'undefined') return;
  const { name, value, rating } = metric || {};
  if (!name || !Number.isFinite(value)) return;
  vitalsQueue.push({
    metric: name,
    value,
    rating,
    page: classifyPage(window.location.pathname),
    device: deviceType(),
    path: window.location.pathname,
  });
  // Ook naar het bestaande Plausible-kanaal (tagged events zijn al geladen)
  window.plausible?.('Web Vital', {
    props: { metric: name, rating, page: classifyPage(window.location.pathname), device: deviceType() },
  });
  if (!flushHooked) {
    flushHooked = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flushVitals();
    });
    window.addEventListener('pagehide', flushVitals);
  }
}

// --- Error monitoring ---

const seenErrors = new Set();
let errorBudget = 10; // max per pageview; beschermt tegen error-loops

export function reportError(error, context = {}) {
  if (typeof window === 'undefined') return;
  try {
    const message = String(error?.message || error || 'Unknown error').slice(0, 500);
    if (!message || message === 'Script error.') return; // cross-origin scripts, niets aan te zien
    const key = `${message}|${context.source || ''}`;
    if (seenErrors.has(key) || errorBudget <= 0) return;
    seenErrors.add(key);
    errorBudget -= 1;
    post('errors', {
      release: RELEASE,
      errors: [{
        message,
        stack: typeof error?.stack === 'string' ? error.stack.slice(0, 4000) : null,
        kind: error?.kind || context.source || 'js',
        page: classifyPage(window.location.pathname),
        device: deviceType(),
        path: window.location.pathname,
      }],
    });
  } catch {
    // nooit doorgooien
  }
}

// Een chunk-load-failure betekent vrijwel altijd: nieuwe deploy, de openstaande
// HTML verwijst naar een chunk die niet meer bestaat. Eén automatische reload
// haalt de nieuwe HTML op; de sessionStorage-guard voorkomt een reload-loop
// wanneer het echt aan het netwerk ligt. Het error-rapport gaat met keepalive
// en overleeft de reload.
function recoverFromStaleChunk(error) {
  const message = String(error?.message || error || '');
  if (!/Failed to load chunk|ChunkLoadError|Loading chunk .+ failed/i.test(message)) return;
  try {
    const last = Number(sessionStorage.getItem('bpb_chunk_reload') || 0);
    if (Date.now() - last < 60000) return;
    sessionStorage.setItem('bpb_chunk_reload', String(Date.now()));
  } catch {
    return; // geen sessionStorage → geen guard mogelijk, dan liever niet reloaden
  }
  window.location.reload();
}

export function initErrorMonitoring() {
  if (typeof window === 'undefined' || window.__bpbTelemetryInit) return;
  window.__bpbTelemetryInit = true;
  window.addEventListener('error', (event) => {
    // Fouten uit third-party scripts (ads, analytics) zijn niet onze code
    if (event.filename && !event.filename.startsWith(window.location.origin) && !event.filename.startsWith('/')) return;
    reportError(event.error || event.message, { source: 'window' });
    recoverFromStaleChunk(event.error || event.message);
  });
  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason, { source: 'unhandledrejection' });
    recoverFromStaleChunk(event.reason);
  });
}
